import ar from 'date-fns/locale/ar';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { ReactElement } from 'react';
import * as React from 'react';
import { registerLocale } from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.min.css';
import styles from './JobInstanceCard.module.css';

import { IWorkingDay } from '@/modules/workingDay/types';
import { NextImage, Typography } from '@/shared/components/atoms';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import { getCardData } from '@/shared/components/molecules/JobInstanceCard/logic';
import { JOB_INSTANCES } from '@/shared/constant/backendStatus';
import routes from '@/shared/constant/routes';

import Arrow from '~/images/shared/leftArrow.svg';

registerLocale('ar', ar);

interface IJobInstanceCardProps {
  jobInstance: IWorkingDay.IJobInstance | undefined;
  showArrow?: boolean;
  showStatus?: boolean;
  showJobId?: boolean;
}

export default function JobInstanceCard({
  jobInstance,
  showStatus = true,
  showArrow = true,
  showJobId = true,
}: IJobInstanceCardProps): ReactElement {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const statusBgColor = jobInstance
    ? JOB_INSTANCES[jobInstance.jobInstanceStatus].color
    : '';
  const statusTextColor = jobInstance
    ? JOB_INSTANCES[jobInstance.jobInstanceStatus].textColor
    : '';
  const cardData = jobInstance ? getCardData(jobInstance, language) : null;

  return jobInstance ? (
    <div className='flex w-full items-center  gap-12 rounded-xl border-xs border-background-100 bg-white px-2 py-16 laptop:px-7'>
      <div className={styles['imageWrapper']}>
        <NextImage
          src={jobInstance?.image}
          alt={jobInstance.jobName}
          width={46}
          height={46}
          imgClassName={styles['image']}
        />
      </div>
      <div className='scrollbar-rounded-lg flex w-full items-center justify-between gap-2 overflow-x-auto whitespace-nowrap overflow-y-hidden  scrollbar-thin scrollbar-thumb-background-400 scrollbar-h-1 '>
        {cardData ? (
          Object.entries(cardData).map(([key, value]) =>
            key === 'jobInstanceId' && !showJobId ? null : (
              <div key={key} className=' max-w-1/5  flex   flex-col gap-4 '>
                <Typography variant='body2' className='text-text-600'>
                  {t(key)}
                </Typography>
                <Typography variant='subtitle2' className='text-text-default '>
                  {String(value) || '-'}
                </Typography>
              </div>
            )
          )
        ) : (
          <></>
        )}

        {showStatus && (
          <div
            className='w-82 rounded-lg py-1   text-center'
            style={{
              backgroundColor: statusBgColor,
              color: statusTextColor,
            }}
          >
            <Typography variant='caption' weight='bold'>
              {t(JOB_INSTANCES[jobInstance.jobInstanceStatus].label)}
            </Typography>
          </div>
        )}

        {showArrow && JOB_INSTANCES[jobInstance.jobInstanceStatus].show && (
          <div className='w-24'>
            <Link
              prefetch={false}
              legacyBehavior
              href={`${routes.workingDay}/${jobInstance?.jobInstanceId}`}
            >
              <a>
                <BaseIcon
                  size='xs'
                  svgClasses='ltr:rotate-180 text-primary-default cursor-pointer'
                >
                  <Arrow />
                </BaseIcon>
              </a>
            </Link>
          </div>
        )}
      </div>
    </div>
  ) : (
    <></>
  );
}
